import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Quote, Target, Linkedin, Newspaper, Presentation, Radio, Sparkles } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton } from "@/components/marketing";
import { BOOK_HREF, CONTACT_HREF } from "@/data/solutions";

const GET = [
  { Icon: Target, title: "Ekspertpositionering", desc: "Vi hjælper jer med at blive den tydelige autoritet inden for jeres område." },
  { Icon: Quote, title: "Skarpe holdninger", desc: "Vi omsætter jeres viden til klare synspunkter, som kunder, medier og beslutningstagere kan huske." },
  { Icon: Linkedin, title: "LinkedIn-tilstedeværelse", desc: "Vi udvikler indhold til virksomhed og nøglepersoner, så jeres ekspertise bliver synlig i hverdagen." },
  { Icon: Newspaper, title: "Presse og debat", desc: "Vi finder vinkler, der kan bruges i medier, debatindlæg, kommentarer og faglige artikler." },
  { Icon: Presentation, title: "Foredrag og webinarer", desc: "Vi hjælper med temaer, oplæg og formater, der styrker jeres autoritet." },
  { Icon: Radio, title: "En konsistent stemme", desc: "Vi skaber en rød tråd i jeres kommunikation, så I bliver genkendelige på tværs af kanaler." },
];

const STEPS = [
  { title: "Vi finder jeres faglige position", desc: "Vi afdækker, hvad I ved, hvad I mener, og hvor I har noget særligt at bidrage med." },
  { title: "Vi vælger de temaer, I skal eje", desc: "Vi identificerer de emner, hvor I kan opbygge en tydelig og troværdig position." },
  { title: "Vi producerer indholdet", desc: "Vi udvikler LinkedIn-opslag, nyhedsbreve, debatindlæg, pressevinkler, artikler og oplæg." },
  { title: "Vi bygger synligheden op over tid", desc: "Thought Leadership skabes ikke med ét opslag. Vi arbejder systematisk med gentagelse, relevans og kvalitet." },
];

const WHEN = [
  "har stor faglig viden, men ikke får den tydeligt nok ud",
  "ønsker at blive mere synlige i markedet",
  "vil styrke tilliden til jeres virksomhed",
  "har nøglepersoner, der kan blive stærkere faglige stemmer",
  "sælger komplekse produkter eller ydelser",
  "gerne vil påvirke en dagsorden",
  "vil være den virksomhed, kunderne tænker på først",
];

export default function ThoughtLeadershipPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
            <span className="relative inline-flex h-1.5 w-1.5">
              <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: "#22D3C5", opacity: 0.4 }} />
              <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#22D3C5" }} />
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Thought Leadership</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance max-w-3xl">
            Bliv den stemme, <span style={{ color: "#22D3C5" }}>jeres kunder lytter til</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
            <p>De virksomheder, der bliver husket, er dem der siger noget, andre har lyst til at lytte til. Vi positionerer din virksomheds nøglepersoner som tydelige eksperter i jeres felt - så kunder, medier og beslutningstagere ved, hvem de skal lytte til, når der er brug for indsigt.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <MButton href={BOOK_HREF} size="lg" data-testid="hero-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
            <MButton href={CONTACT_HREF} size="lg" variant="secondary" data-testid="hero-kontakt">Kontakt os</MButton>
          </motion.div>
        </div>
      </section>

      {/* Hvad er Thought Leadership */}
      <section className="relative z-10 border-y border-border bg-card">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad er det?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-6">Hvad er Thought Leadership?</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
            <p>Thought Leadership handler ikke om at larme mest. Det handler om at være relevant, troværdig og tydelig over tid.</p>
            <p>Vi hjælper jer med at finde de temaer, holdninger og historier, som I kan eje i markedet. Derefter omsætter vi dem til indhold, der styrker jeres faglige position og gør jer synlige dér, hvor jeres målgruppe allerede orienterer sig.</p>
            <p className="text-foreground">Det kan være på LinkedIn, i nyhedsbreve, i debatindlæg, i pressen, på webinarer eller i faglige oplæg.</p>
          </div>
        </div>
      </section>

      {/* Hvad får I? */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvad får I?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Fra faglig viden til tydelig autoritet</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {GET.map((g, i) => (
            <motion.div key={g.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
              className="rounded-2xl border border-border bg-card p-6">
              <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center mb-4">
                <g.Icon className="h-5 w-5 text-foreground" strokeWidth={1.75} />
              </div>
              <h3 className="font-display text-lg font-medium">{g.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{g.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Sådan arbejder vi */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <div className="max-w-2xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Sådan arbejder vi</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">En systematisk vej til synlighed</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="text-5xl font-display font-medium text-muted-foreground/25">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-lg font-medium mt-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Hvornår giver det mening */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">Hvornår giver det mening?</p>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Thought Leadership er oplagt, når I:</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {WHEN.map((w, i) => (
            <motion.div key={w} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.35, delay: (i % 2) * 0.06 }}
              className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4">
              <div className="h-6 w-6 rounded-full bg-foreground flex items-center justify-center shrink-0">
                <Check className="h-3.5 w-3.5 text-background" strokeWidth={2.5} />
              </div>
              <span className="text-foreground">{w}</span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Kombinér med Aicom AI */}
      <section className="relative z-10 border-t border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-20 text-center">
          <div className="inline-flex h-12 w-12 rounded-xl bg-secondary items-center justify-center mb-6">
            <Sparkles className="h-5 w-5 text-foreground" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-5">Kombinér med Aicom AI</h2>
          <div className="space-y-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            <p>Aicom AI kan hjælpe med at omsætte jeres faglige position til idéer, LinkedIn-opslag, nyhedsbreve, pressehistorier og kampagner.</p>
            <p>Vi bruger AI til at effektivisere analyse, idéudvikling og produktion - men jeres position, stemme og holdninger skal være ægte. Det er kombinationen af faglighed, strategi og AI, der gør arbejdet både hurtigere og stærkere.</p>
          </div>
          <div className="mt-8">
            <MButton to="/losninger/aicom-ai" variant="secondary" data-testid="link-aicom-ai">Læs om Aicom AI <ArrowRight className="h-4 w-4" /></MButton>
          </div>
        </div>
      </section>

      {/* Afsluttende CTA */}
      <section className="relative z-10 max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance mb-5">Skal I være den stemme, markedet lytter til?</h2>
        <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          Lad os tage en uforpligtende snak om, hvordan I kan opbygge en stærkere ekspertposition og blive mere synlige over for de kunder, medier og beslutningstagere, der betyder mest.
        </p>
        <div className="mt-8">
          <MButton href={BOOK_HREF} size="lg" data-testid="footer-book">Book et møde <ArrowRight className="h-4 w-4" /></MButton>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
